import React from 'react'
import Text from '~/views/components/ui/Text'
import {
  CardBodyContainer,
  CardBodyContent,
  CardHeaderContainer,
  ContainerCard,
  LineDivider,
} from '~/views/components/ProjectOverview/components/ProjectOverviewCard'
import BillableAllottedContent from './components/BillableAllottedContent/BillableAllottedContent'
import AccruedBilledContent from './components/AccruedBilledContent/AccruedBilledContent'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

type ProjectOverviewBillingCardProps = {
  accruedBilled?: number | null
  billableHours?: number | null
  allottedHours?: number | null
  showHoursToCustomers?: boolean | null
  isInternalUser?: boolean
  loading?: boolean | null
}

export default function ProjectOverviewBillingCard(
  props: ProjectOverviewBillingCardProps,
) {
  const {
    accruedBilled,
    billableHours,
    allottedHours,
    isInternalUser,
    showHoursToCustomers,
    loading,
  } = props

  if (!isInternalUser && !showHoursToCustomers) {
    return null
  }

  return (
    <ContainerCard data-component-name="billing-card">
      <BillableAllottedHeader />

      {isInternalUser ? (
        <InternalBillableAllottedBody
          accruedBilled={accruedBilled}
          billableHours={billableHours}
          allottedHours={allottedHours}
          loading={loading === true}
        />
      ) : (
        <ExternalBillableAllottedBody
          billableHours={billableHours}
          allottedHours={allottedHours}
          loading={loading === true}
        />
      )}
    </ContainerCard>
  )
}

function BillableAllottedHeader() {
  const [getCommonLabel] = useStaticLabels(['common'])
  return (
    <CardHeaderContainer>
      <Text data-component-name="cost-title" weight="bold" size="large">
        {getCommonLabel('billing')}
      </Text>
    </CardHeaderContainer>
  )
}

type BillableAllottedBodyProps = {
  accruedBilled?: number | null
  billableHours?: number | null
  allottedHours?: number | null
  loading: boolean
}

function InternalBillableAllottedBody({
  accruedBilled,
  billableHours,
  allottedHours,
  loading,
}: BillableAllottedBodyProps) {
  const shouldDisplayAllottedContent = allottedHours ? allottedHours > 0 : false
  const shouldDisplayDivider = shouldDisplayAllottedContent

  return (
    <CardBodyContainer>
      <CardBodyContent>
        <AccruedBilledContent accruedBilled={accruedBilled} loading={loading} />
        {shouldDisplayDivider && <LineDivider data-testid="line-divider" />}
        {shouldDisplayAllottedContent && (
          <BillableAllottedContent
            billableHours={billableHours}
            allottedHours={allottedHours}
          />
        )}
      </CardBodyContent>
    </CardBodyContainer>
  )
}

function ExternalBillableAllottedBody({
  billableHours,
  allottedHours,
}: Omit<BillableAllottedBodyProps, 'accruedBilled'>) {
  const shouldDisplayAllottedContent = allottedHours ? allottedHours > 0 : false

  return (
    <CardBodyContainer>
      <CardBodyContent>
        {shouldDisplayAllottedContent && (
          <BillableAllottedContent
            billableHours={billableHours}
            allottedHours={allottedHours}
          />
        )}
      </CardBodyContent>
    </CardBodyContainer>
  )
}
